import { service } from './base-service'
import store from '@/store'
import moment from 'moment'
import i18n from '@/plugins/i18n'
import * as _ from 'lodash'
import { downloadFile } from '@/utils/download-file'

const getTermKey = (termKey = false) => {
  const locale = store.getters.locale
  if (!termKey) {
    termKey = locale === 'pt-BR' ? 'name' : 'internationalName'
  }
  return termKey
}

const getAllCountries = () => {
  return service.get('/country')
}

const getCountries = (term, termKey = false) => {
  return service.get(`/country?${getTermKey(termKey)}=${term}`)
}

const getCountryByLocale = (country, termKey = false) => {
  termKey = getTermKey(termKey)
  return country[termKey]
}

const getCountryLabel = async (countryName) => {
  const termKey = getTermKey()
  const { data } = await getCountries(countryName, 'internationalName')
  if (data && data.length > 0) {
    return data.find((country) => country['internationalName'] === countryName)[
      termKey
    ]
  }
}

const getDate = (date) => {
  if (date === null || date === undefined) {
    return ''
  }
  if (store.getters.locale === 'en') {
    return '' + moment.utc(date).format('YYYY/MM/DD').toString()
  }
  return '' + moment.utc(date).format('DD/MM/YYYY').toString()
}

const getTime = (date) => {
  return '' + moment.utc(date).utcOffset(-3).format('HH:mm:ss').toString()
}

const getDateTime = (date) => {
  if (date === null || date === undefined) return ''
  return (
    '' + moment.utc(date).utcOffset(-3).format('DD/MM/YYYY HH:mm').toString()
  )
}

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
}

const getDateAddDay = (date) => {
  if (store.getters.locale === 'en') {
    return '' + moment(date).add(1, 'd').format('YYYY/MM/DD').toString()
  }
  return '' + moment(date).add(1, 'd').format('DD/MM/YYYY').toString()
}

const formatCPF = (cpf) => {
  // Remover caracteres não numéricos
  cpf = cpf.replace(/\D/g, '')

  // Garantir que tenha 11 dígitos
  cpf = cpf.padStart(11, '0')

  // Adicionar pontos e traço de acordo com o formato do CPF
  cpf = cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')

  return cpf
}

const getSelectOptionsMargin = (group) => {
  const code = group && group.code ? group.code.split('.') : []
  let spaces = ''
  for (let i = 0; i < code.length - 1; i++) {
    spaces += '\xa0\xa0\xa0'
  }
  return spaces
}

const getUnitySelectOptionsMargin = (struct) => {
  const code = struct.Codigo.split('.')
  let spaces = ''
  for (let i = 0; i < code.length - 1; i++) {
    spaces += '\xa0\xa0\xa0'
  }
  return spaces
}

const formatCurrency = (value) => {
  const locale = store.getters.locale
  const formattedValue = value.toFixed(2).replace('.', ',')
  return locale === 'pt-BR' ? `R$ ${formattedValue}` : `$ ${formattedValue}`
}

const parseInitiatives = (initiatives) => {
  return initiatives
    .map((initiative) => {
      return initiative === 'company'
        ? i18n.t('empresa')
        : i18n.t('colaborador')
    })
    .join(' - ')
}

const downloadData = (data, fileName, type = '') => {
  const url = window.URL.createObjectURL(new Blob([data], { type }))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
}

const removeDemarcoRecordsIfMaster = (docs, clientPath = 'client') => {
  if (store.getters.isMaster) {
    return docs
  }

  return docs.filter(
    (doc) => _.get(doc, clientPath) !== process.env.VUE_APP_DEMARCO_CODE
  )
}

function isValidCPF(cpfString) {
  // Remove todos os caracteres não numéricos (se for formatado)
  cpfString = cpfString.replace(/\D/g, '')

  // Verifica se o CPF possui exatamente 11 dígitos
  if (cpfString.length !== 11) return false

  // Verifica se todos os dígitos são iguais (ex: "00000000000" ou "11111111111")
  if (/^(\d)\1{10}$/.test(cpfString)) return false

  // Função auxiliar para calcular um dígito verificador
  const calculateDigit = (cpf, factor) => {
    let sum = 0
    for (let i = 0; i < factor - 1; i++) {
      sum += parseInt(cpf[i]) * (factor - i)
    }
    const rest = (sum * 10) % 11
    return rest === 10 ? 0 : rest
  }

  // Calcula o primeiro dígito verificador
  const firstDigit = calculateDigit(cpfString, 10)
  if (firstDigit !== parseInt(cpfString[9])) return false

  // Calcula o segundo dígito verificador
  const secondDigit = calculateDigit(cpfString, 11)
  if (secondDigit !== parseInt(cpfString[10])) return false

  return true
}

function formatDocument(cpf) {
  // Remove qualquer caractere que não seja número
  const numericCPF = cpf.replace(/\D/g, '')

  // Verifica se o CPF é válido
  if (!isValidCPF(numericCPF)) {
    return cpf // Retorna o valor original se não for um CPF válido
  }

  // Aplica a máscara ao CPF
  return numericCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

function extractFileNameFromContentDisposition(dispositionHeader) {
  if (dispositionHeader) {
    const match = dispositionHeader.match(/filename="?([^"]+)"?/)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

export {
  getAllCountries,
  getCountries,
  getCountryByLocale,
  getCountryLabel,
  getDate,
  getTime,
  getDateTime,
  formatBytes,
  getDateAddDay,
  formatCPF,
  getSelectOptionsMargin,
  getUnitySelectOptionsMargin,
  formatCurrency,
  parseInitiatives,
  downloadData,
  downloadFile,
  removeDemarcoRecordsIfMaster as removeDemarcoRecords,
  isValidCPF,
  formatDocument,
  extractFileNameFromContentDisposition
}
